import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { getDisplayedUnit } from 'commons/src/commonFunctions';
import SectionHeader from 'commons/src/components/headers/SectionHeader';
import ResponseBox from 'commons/src/components/responseMessages/ResponseBox';
import SensorIcon from 'commons/src/components/sensors/SensorIcon';
import { sensorFullNameKey } from 'commons/src/constants';
import { SensorTypes, ThresholdsResourceScope } from 'commons/src/models/commonEnums';
import { SpaceThresholds } from 'commons/src/models/customThresholdModels';
import ThresholdSetting from '../../components/thresholds/ThresholdSetting';
import styles from './DeviceSensorThresholdForm.module.scss';
import useDeviceThresholdMgmt from './useDeviceThresholdMgmt';

export type DeviceSensorThresholdFormProps = {
    sensorType: SensorTypes;
    thresholds: SpaceThresholds;
    serialNumber: string;
    readOnly?: boolean;
    onUpdateSuccess?: () => void;
};

const DeviceSensorThresholdForm = ({
    sensorType,
    thresholds,
    serialNumber,
    readOnly = false,
    onUpdateSuccess,
}: DeviceSensorThresholdFormProps): ReactElement => {
    const { t: txt } = useTranslation();
    const { thresholdOptions, selectedValues, handleSetSelectedValues, handleOnSave, isLoading, isSaved, error } =
        useDeviceThresholdMgmt({
            sensorType,
            thresholds,
            serialNumber,
            updateSuccessAction: onUpdateSuccess,
        });

    const unitDisplay = getDisplayedUnit(sensorType, thresholds.unit as string);

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <SectionHeader
                    headerType={4}
                    headerName=""
                    customHeader={
                        <>
                            {txt(sensorFullNameKey(sensorType))} <span className={styles.unit}>({unitDisplay})</span>
                        </>
                    }
                    leadingIcon={<SensorIcon sensor={sensorType} />}
                    key={`thresholdHeader${sensorType}`}
                />
            </div>
            <p className={styles.description}>{txt(`CustomThresholds.${sensorType}Description`)}</p>
            <ThresholdSetting
                sensorType={sensorType}
                ranges={thresholds.ranges}
                thresholdOptions={thresholdOptions}
                customOptions={thresholds.customOptions}
                selectedValues={selectedValues}
                setSelectedValues={handleSetSelectedValues}
                isParentScope={false}
                scopeModified={false}
                buttonText={txt(`CustomThresholds.ThresholdsText${ThresholdsResourceScope.DEVICE}`)}
                loading={isLoading}
                onSave={handleOnSave}
                isSaved={isSaved}
                fullwidthButton={false}
                allowedToEdit={!readOnly}
                unit={thresholds.unit as string}
            />
            {error && (
                <div className={styles.error}>
                    <ResponseBox text={`ErrorCodes.${error.error}`} />
                </div>
            )}
        </div>
    );
};

export default DeviceSensorThresholdForm;
